import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import {
  FaUserCircle,
  FaEdit,
  FaCamera,
  FaTimes,
  FaFire,
  FaBuilding,
  FaIdBadge,
  FaUserTag,
  FaCheck
} from "react-icons/fa";
import axios from "axios";
import openNotification from "components/utils/CustomNotification";
import Cropper from "react-easy-crop";
import heic2any from "heic2any";
import ConfirmationDialog from "../utils/ConfirmationDialog"

// --- Types & Defaults ---

const timezones = {
  "Africa/Cairo": "Africa/Cairo",
  "Africa/Johannesburg": "Africa/Johannesburg",
  "Africa/Lagos": "Africa/Lagos",
  "Africa/Nairobi": "Africa/Nairobi",
  "America/Argentina/Buenos_Aires": "America/Argentina/Buenos_Aires",
  "America/Argentina/Cordoba": "America/Argentina/Cordoba",
  "America/Mexico_City": "America/Mexico_City",
  "America/Sao_Paulo": "America/Sao_Paulo",
  "US/Alaska (America/Anchorage)": "America/Anchorage",
  "US/Pacific (America/Los_Angeles)": "America/Los_Angeles",
  "US/Mountain (America/Denver)": "America/Denver",
  "US/Central (America/Chicago)": "America/Chicago",
  "US/Eastern (America/New_York)": "America/New_York",
  "Asia/Amman": "Asia/Amman",
  "Asia/Baghdad": "Asia/Baghdad",
  "Asia/Bangkok": "Asia/Bangkok",
  "Asia/Beirut": "Asia/Beirut",
  "Asia/China": "Asia/China",
  "Asia/Dubai": "Asia/Dubai",
  "Asia/Hong_Kong": "Asia/Hong_Kong",
  "Asia/Jakarta": "Asia/Jakarta",
  "Asia/Kolkata": "Asia/Kolkata",
  "Asia/Kuala_Lumpur": "Asia/Kuala_Lumpur",
  "Asia/Manila": "Asia/Manila",
  "Asia/Singapore": "Asia/Singapore",
  "Asia/Tokyo": "Asia/Tokyo",
  "Australia/Sydney": "Australia/Sydney",
  "Europe/Amsterdam": "Europe/Amsterdam",
  "Europe/Berlin": "Europe/Berlin",
  "Europe/Brussels": "Europe/Brussels",
  "Europe/Lisbon": "Europe/Lisbon",
  "Europe/London": "Europe/London",
  "Europe/Madrid": "Europe/Madrid",
  "Europe/Paris": "Europe/Paris",
  "Europe/Rome": "Europe/Rome",
  "Europe/Stockholm": "Europe/Stockholm",
  "Europe/Vienna": "Europe/Vienna",
  "Pacific/Auckland": "Pacific/Auckland",
  "Pacific/Fiji": "Pacific/Fiji",
  "Pacific/Tarawa": "Pacific/Tarawa",
  "Pacific/Tongatapu": "Pacific/Tongatapu",
};

interface CorporateData {
  org_uid: string;
  first_name: string;
  alt_email: string;
  alt_phone: string;
  blood_group: string;
  city: string;
  country_code: string;
  date_of_birth: string;
  display_name: string;
  email: string;
  employee_number: number | string;
  job_title: string;
  joining_date: string;
  last_name: string;
  mobile_phone: string;
  nationality: string;
  department: string;
  project: string;
  team: string;
  reports_to: string;
  reports_to_username: string;
  current_address: {
    addressLine1: string;
    addressLine2: string;
    countryCode: string;
    city: string;
    state: string;
    zip: number | string;
  };
}

const defaultCorporateData: CorporateData = {
  org_uid: "",
  first_name: "",
  alt_email: "",
  alt_phone: "",
  blood_group: "",
  city: "",
  country_code: "",
  date_of_birth: "",
  display_name: "",
  email: "",
  employee_number: "",
  job_title: "",
  joining_date: "",
  last_name: "",
  mobile_phone: "",
  nationality: "",
  department: "",
  project: "",
  team: "",
  reports_to: "",
  reports_to_username: "",
  current_address: {
    addressLine1: "",
    addressLine2: "",
    countryCode: "",
    city: "",
    state: "",
    zip: "",
  },
};

interface TempUser {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  displayName?: string;
  alt_phone?: string;
  alt_email?: string;
  phone?: string;
  address?: string;
  timezone?: string;
  [key: string]: any;
}

const UserProfilePage: React.FC = () => {
  const beAddress = window._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL;
  const sessionUid = localStorage.getItem("session_uid") || "";
  const userUid = localStorage.getItem("user_uid") || "";
  const org_uid = localStorage.getItem("org_uid") || "";

  // --- State ---
  const [user, setUser] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    displayName: "",
    email: "",
    timezone: "",
    profilePic: "https://via.placeholder.com/150",
    org_uid: "",
    user_type: "",
    address: "",
    privilege: "",
    user_role: "",
    phone: "",
    alt_phone: "",
    alt_email: "",
    history_uid: "",
    gender: "",
    date_of_birth: "",
  });

  const [tempUser, setTempUser] = useState<TempUser>({});
  const [corporateData, setCorporateData] = useState<CorporateData>(defaultCorporateData);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [currentStreak, setCurrentStreak] = useState<string>("0");

  // Image States
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [imgDimensions, setImgDimensions] = useState<{ width: number; height: number } | null>(null);

  // Dialog States
  const [showDeleteUserDialog, setShowDeleteUserDialog] = useState(false);
  const [showDeleteConsentDialog, setShowDeleteConsentDialog] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false); // Actions modal
  const [isImageViewOpen, setIsImageViewOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const isDefaultImage =
    !user.profilePic ||
    user.profilePic === "https://via.placeholder.com/150" ||
    user.profilePic === "data:image/png;base64,R0lGODlhAQABAIABAAAAAP///yH5BAEAAAEALAAAAAABAAEAAAICRAEAOw==";

  // --- Effects ---
  useEffect(() => {
    const streak = localStorage.getItem("org_current_streak");
    if (streak) setCurrentStreak(streak);
  }, []);

  const fetchCorporateData = async (emailToFilter: string) => {
    setLoading(true);
    setErrorMessage("");
    try {
      const response = await axios.get(`${beAddress}/api/employee-profile`, {
        headers: {
          "Content-Type": "application/json",
          "user-uid": userUid,
        },
        withCredentials: true,
        params: {
          org_uid: org_uid,
          users_list: [userUid] // Filter by userUid if backend supports, otherwise filter by email below
        },
      });

      if (response.data.status === "Success" && Array.isArray(response.data.data)) {
        // Find specific user by email
        const matchedEmployee = response.data.data.find(
          (emp: any) => emp.email?.toLowerCase() === emailToFilter?.toLowerCase()
        );

        if (matchedEmployee) {
          setCorporateData(matchedEmployee);
        } else {
          console.warn("User not found in corporate list, showing default/N/A");
          setCorporateData(defaultCorporateData);
        }
      } else {
        setCorporateData(defaultCorporateData);
      }
    } catch (error) {
      console.error("Error fetching corporate data", error);
      setCorporateData(defaultCorporateData);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserProfileImage = async () => {
    try {
      const response = await axios.get(`${beAddress}/api/user-profile-images`, {
        headers: {
          "Content-Type": "application/json",
          "user-uid": userUid,
        },
        withCredentials: true,
        params: { icon_img: false },
      });
      const result = response.data;
      if (result.status === "Success" && result.data.length > 0) {
        const imageData = result.data[0].image;
        if (imageData && imageData !== "R0lGODlhAQABAIABAAAAAP///yH5BAEAAAEALAAAAAABAAEAAAICRAEAOw==") {
          setUser((prev) => ({ ...prev, profilePic: `data:image/png;base64,${imageData}` }));
        }
      }
    } catch (error) {
      console.error("Error fetching user profile image:", error);
    }
  };

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await fetch(`${beAddress}/api/user-profile`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "user-uid": userUid,
          },
          credentials: "include",
        });
        const result = await response.json();

        if (result.status === "Success" && typeof result.data === "object") {
          const userData = result.data;
          setUser((prev) => ({
            ...prev,
            firstName: userData.first_name || "",
            middleName: userData.middle_name || "",
            lastName: userData.last_name || "",
            displayName: userData.display_name || "",
            email: userData.email || "",
            timezone: userData.timezone || "UTC",
            org_uid: userData.org_uid || "",
            user_type: userData.user_type || "",
            address: userData.address || "",
            privilege: userData.privilege || "",
            user_role: userData.user_role || "",
            phone: userData.phone || "",
            alt_phone: userData.alt_phone || "",
            alt_email: userData.alt_email || "",
            history_uid: userData.history_uid || "",
            gender: userData.gender || "",
            date_of_birth: userData.date_of_birth || "",
          }));

          localStorage.setItem("org_uid", userData.org_uid || "");
          localStorage.setItem("history_uid", userData.history_uid || "");

          // Chain call to corporate data
          if (userData.email) {
            fetchCorporateData(userData.email);
          }
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
      }
    };

    fetchUserProfile();
    fetchUserProfileImage();
  }, []);

  // --- Handlers ---

  const handleEdit = (field: string, value: string) => {
    setEditingField(field);
    setTempUser((prev) => ({ ...prev, [field]: value }));
  };

  const handleCancelEdit = () => {
    setEditingField(null);
  };

  // --- Updated Logic ---

  const handleSingleFieldSave = async (fieldKey: string, dbKey: string) => {
    // Get the new value from tempUser, fallback to current user value if undefined
    const newValue = tempUser[fieldKey] !== undefined ? tempUser[fieldKey] : user[fieldKey as keyof typeof user];

    // Basic Validation
    if ((fieldKey === 'firstName' || fieldKey === 'lastName') && !newValue?.trim()) {
      openNotification({ type: "error", message: "This field cannot be empty" });
      return;
    }

    try {
      // Construct payload with identifiers, REQUIRED user_type, and the changed field
      const payload = {
        user_uid: userUid,
        org_uid: user.org_uid,
        user_type: user.user_type, // <--- ADDED THIS (Required by API)
        [dbKey]: newValue,
      };

      const response = await axios.put(
        `${beAddress}/api/user-profile`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            "user-uid": userUid,
          },
          withCredentials: true,
        }
      );

      if (response.data.status === "Success") {
        openNotification({ type: "success", message: "Updated successfully!" });

        // Update local state
        setUser((prev) => ({ ...prev, [fieldKey]: newValue }));

        // Clear temp state
        setEditingField(null);
        setTempUser({});
      } else {
        openNotification({ type: "error", message: response.data.message || "Update failed." });
      }
    } catch (error) {
      console.error(error);
      openNotification({ type: "error", message: "Error updating profile." });
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    let file = event.target.files?.[0];
    if (!file) return;

    const MAX_FILE_SIZE = 5 * 1024 * 1024;

    if (file.size > MAX_FILE_SIZE) {
      openNotification({
        type: "error",
        message: "File is too large. Please upload an image smaller than 5MB.",
      });
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    const readFileAsDataURL = (inputFile: File): Promise<string> =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error("FileReader error"));
        reader.readAsDataURL(inputFile);
      });

    const loadImage = (src: string): Promise<HTMLImageElement> =>
      new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error("Image failed to load"));
        img.src = src;
      });

    try {
      const base64String = await readFileAsDataURL(file);
      const img = await loadImage(base64String);
      setImgDimensions({ width: img.naturalWidth, height: img.naturalHeight });
      setPreviewImage(base64String);
      setIsCropModalOpen(true);
    } catch (err) {
      console.error("Image upload error", err);
      openNotification({ type: "error", message: "Failed to load image." });
    }
  };

  const handleCropSave = async () => {
    if (!previewImage || !croppedAreaPixels) return;
    try {
      const croppedImage = await getCroppedImg(previewImage, croppedAreaPixels);

      // Upload logic
      const formData = new FormData();
      formData.append("org_uid", String(user.org_uid));
      formData.append("image", croppedImage);

      const response = await axios.post(`${beAddress}/api/user-profile-images`, formData, {
        headers: { "user-uid": userUid },
        withCredentials: true,
      });

      if (response.data.status === "Success") {
        setUser((prev) => ({ ...prev, profilePic: croppedImage }));
        localStorage.setItem("profileImage", croppedImage);
        window.dispatchEvent(new CustomEvent("profileImageUpdated", { detail: croppedImage }));
        openNotification({ type: "success", message: "Profile picture updated!" });
      } else {
        // --- NEW: Handle API Failure Response ---
        // Displays "Update user profile images failed" or fallback message
        openNotification({
          type: "error",
          message: response.data.message || "Image upload failed"
        });
      }
    } catch (error) {
      console.error("Error saving crop", error);
      openNotification({ type: "error", message: "An error occurred while uploading the image." });
    }
    setIsCropModalOpen(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const getCroppedImg = (imageSrc: string, pixelCrop: any): Promise<string> => {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.src = imageSrc;
      image.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = pixelCrop.width;
        canvas.height = pixelCrop.height;
        const ctx = canvas.getContext("2d");
        if (!ctx) return reject("No ctx");
        ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, pixelCrop.width, pixelCrop.height);
        resolve(canvas.toDataURL("image/jpeg", 0.95));
      };
      image.onerror = reject;
    });
  };

  const handleDeleteProfilePic = async () => {
    // (Delete logic)
    try {
      const response = await axios.delete(`${beAddress}/api/user-profile-images`, {
        headers: { "Content-Type": "application/json", "user-uid": userUid }, withCredentials: true,
        data: String(user.org_uid)
      });
      if (response.data.status === "Success") {
        setUser(prev => ({ ...prev, profilePic: "https://via.placeholder.com/150" }));
        localStorage.removeItem("profileImage");
        openNotification({ type: "success", message: "Profile picture removed" });
      }
    } catch (e) { console.error(e); }
    setIsModalOpen(false);
  };

  const handleModalAction = (action: string) => {
    if (action === 'view') {
      setIsImageViewOpen(true);
      setIsModalOpen(false);
    }
    if (action === 'change') {
      fileInputRef.current?.click();
      setIsModalOpen(false);
    }
    if (action === 'delete') {
      // Open the confirmation dialog instead of deleting directly
      setIsDeleteConfirmOpen(true);
      setIsModalOpen(false); // Close the initial options modal
    }
  };

  const confirmDelete = () => {
    handleDeleteProfilePic();
    setIsDeleteConfirmOpen(false);
  };

  return (
    <PageContainer>
      {/* Header Section */}
      <HeaderSection>
        <ProfileCard>
          <ProfileImageWrapper>
            {isDefaultImage ? (
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#f0f0f0",
                  color: "#999",
                  cursor: "pointer",
                  border: "3px solid #fff",
                  boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                }}
                onClick={() => setIsModalOpen(true)}
              >
                <FaUserCircle size={60} />
              </div>
            ) : (
              <img
                src={user.profilePic}
                alt="Profile"
                onClick={() => setIsModalOpen(true)}
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                  setUser((prev) => ({
                    ...prev,
                    profilePic: "https://via.placeholder.com/150",
                  }));
                }}
              />
            )}
            <EditIconBtn onClick={() => setIsModalOpen(true)}>
              <FaCamera />
            </EditIconBtn>
          </ProfileImageWrapper>

          <ProfileInfo>
            <Name>
              {user.firstName} {user.lastName}
            </Name>
            <Email>{user.email}</Email>
            <StreakBadge>
              <FaFire /> {currentStreak} Day Streak
            </StreakBadge>
          </ProfileInfo>
        </ProfileCard>

        <StatsGrid>
          <StatCard>
            <StatIcon color="#3b82f6"><FaBuilding /></StatIcon>
            <StatContent>
              <label>Organization</label>
              <strong>{user.org_uid || "N/A"}</strong>
            </StatContent>
          </StatCard>
          <StatCard>
            <StatIcon color="#10b981"><FaUserTag /></StatIcon>
            <StatContent>
              <label>Role</label>
              <strong>{user.user_role || "N/A"}</strong>
            </StatContent>
          </StatCard>
          <StatCard>
            <StatIcon color="#8b5cf6"><FaIdBadge /></StatIcon>
            <StatContent>
              <label>Privilege</label>
              <strong>{user.privilege || "N/A"}</strong>
            </StatContent>
          </StatCard>
        </StatsGrid>
      </HeaderSection>

      {/* Main Content Grid */}
      <ContentGrid>
        {/* Personal Info */}
        {/* Personal Info */}
        <SectionCard>
          <SectionHeader>
            <h3>Personal Information</h3>
            {/* Global Save/Cancel buttons removed from here */}
          </SectionHeader>
          <FieldsContainer>
            {/* Row 1 */}
            <EditableField
              label="First Name"
              value={user.firstName}
              isEditing={editingField === "firstName"}
              tempValue={tempUser.firstName}
              maxLength={30}
              onEdit={() => handleEdit("firstName", user.firstName)}
              onChange={(v: string) => setTempUser((prev) => ({ ...prev, firstName: v }))}
              onCancel={handleCancelEdit}
              onSave={() => handleSingleFieldSave("firstName", "first_name")}
            />
            <EditableField
              label="Middle Name"
              value={user.middleName}
              isEditing={editingField === "middleName"}
              tempValue={tempUser.middleName}
              maxLength={30}
              onEdit={() => handleEdit("middleName", user.middleName)}
              onChange={(v: string) => setTempUser((prev) => ({ ...prev, middleName: v }))}
              onCancel={handleCancelEdit}
              onSave={() => handleSingleFieldSave("middleName", "middle_name")}
            />

            {/* Row 2 */}
            <EditableField
              label="Last Name"
              value={user.lastName}
              isEditing={editingField === "lastName"}
              tempValue={tempUser.lastName}
              maxLength={30}
              onEdit={() => handleEdit("lastName", user.lastName)}
              onChange={(v: string) => setTempUser((prev) => ({ ...prev, lastName: v }))}
              onCancel={handleCancelEdit}
              onSave={() => handleSingleFieldSave("lastName", "last_name")}
            />
            <EditableField
              label="Display Name"
              value={user.displayName}
              isEditing={editingField === "displayName"}
              tempValue={tempUser.displayName}
              maxLength={50}
              onEdit={() => handleEdit("displayName", user.displayName)}
              onChange={(v: string) => setTempUser((prev) => ({ ...prev, displayName: v }))}
              onCancel={handleCancelEdit}
              onSave={() => handleSingleFieldSave("displayName", "display_name")}
            />

            {/* Row 3 - Phone */}
            <EditableField
              label="Phone"
              value={user.phone}
              isEditing={editingField === "phone"}
              tempValue={tempUser.phone}
              maxLength={15}
              inputType="tel"
              onEdit={() => handleEdit("phone", user.phone)}
              onChange={(v: string) => {
                const sanitized = v.replace(/\D/g, '');
                setTempUser((prev) => ({ ...prev, phone: sanitized }));
              }}
              onCancel={handleCancelEdit}
              onSave={() => handleSingleFieldSave("phone", "phone")}
            />
            <EditableField
              label="Alt Phone"
              value={user.alt_phone}
              isEditing={editingField === "alt_phone"}
              tempValue={tempUser.alt_phone}
              maxLength={15}
              inputType="tel"
              onEdit={() => handleEdit("alt_phone", user.alt_phone)}
              onChange={(v: string) => {
                if (/^\d*$/.test(v)) setTempUser((prev) => ({ ...prev, alt_phone: v }));
              }}
              onCancel={handleCancelEdit}
              onSave={() => handleSingleFieldSave("alt_phone", "alt_phone")}
            />

            {/* Row 4: Full Width Address */}
            <div style={{ gridColumn: "1 / -1" }}>
              <EditableField
                label="Address"
                value={user.address}
                isEditing={editingField === "address"}
                tempValue={tempUser.address}
                maxLength={150}
                onEdit={() => handleEdit("address", user.address)}
                onChange={(v: string) => setTempUser((prev) => ({ ...prev, address: v }))}
                onCancel={handleCancelEdit}
                onSave={() => handleSingleFieldSave("address", "address")}
              />
            </div>

            {/* Row 5 */}
            <EditableField
              label="Alternate Email"
              value={user.alt_email}
              isEditing={editingField === "alt_email"}
              tempValue={tempUser.alt_email}
              maxLength={100}
              onEdit={() => handleEdit("alt_email", user.alt_email)}
              onChange={(v: string) => setTempUser((prev) => ({ ...prev, alt_email: v }))}
              onCancel={handleCancelEdit}
              onSave={() => handleSingleFieldSave("alt_email", "alt_email")}
            />
            <EditableField
              label="Timezone"
              value={user.timezone}
              isEditing={editingField === "timezone"}
              tempValue={tempUser.timezone}
              type="select"
              options={timezones}
              onEdit={() => handleEdit("timezone", user.timezone)}
              onChange={(v: string) => setTempUser((prev) => ({ ...prev, timezone: v }))}
              onCancel={handleCancelEdit}
              onSave={() => handleSingleFieldSave("timezone", "timezone")}
            />

            {/* Row 6: Gender & DOB */}
            <div
              style={{
                gridColumn: "1 / -1",
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "20px",
              }}
            >
              <EditableField
                label="Gender"
                value={user.gender}
                isEditing={editingField === "gender"}
                tempValue={tempUser.gender}
                type="select"
                options={{ Male: "male", Female: "female", Others: "others" }}
                onEdit={() => handleEdit("gender", user.gender)}
                onChange={(v: string) => setTempUser((prev) => ({ ...prev, gender: v }))}
                onCancel={handleCancelEdit}
                onSave={() => handleSingleFieldSave("gender", "gender")}
              />
              <EditableField
                label="Month & Year of Birth"
                value={user.date_of_birth}
                isEditing={editingField === "date_of_birth"}
                tempValue={tempUser.date_of_birth}
                type="month"
                onEdit={() => handleEdit("date_of_birth", user.date_of_birth)}
                onChange={(v: string) => setTempUser((prev) => ({ ...prev, date_of_birth: v }))}
                onCancel={handleCancelEdit}
                onSave={() => handleSingleFieldSave("date_of_birth", "date_of_birth")}
              />
            </div>
          </FieldsContainer>
        </SectionCard>

        {/* Corporate Info */}
        <SectionCard>
          <SectionHeader>
            <h3>Corporate Information</h3>
          </SectionHeader>
          <CorporateGrid>
            <ReadOnlyField label="Display Name" value={corporateData.display_name} />
            <ReadOnlyField label="Job Title" value={corporateData.job_title} />
            <ReadOnlyField label="Department" value={corporateData.department} />
            <ReadOnlyField label="Employee ID" value={corporateData.employee_number} />
            <ReadOnlyField label="Manager" value={corporateData.reports_to_username} />
            <ReadOnlyField label="Project" value={corporateData.project} />
            <ReadOnlyField label="Work Location" value={corporateData.current_address?.city} />
            <ReadOnlyField label="Team" value={corporateData.team} />
            <ReadOnlyField label="Nationality" value={corporateData.nationality} />
          </CorporateGrid>
        </SectionCard>
      </ContentGrid>

      {/* Hidden Inputs & Modals */}
      <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleImageUpload} accept="image/*" />

      {isModalOpen && (
        <ModalOverlay onClick={() => setIsModalOpen(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Profile Photo</ModalTitle>

            {!isDefaultImage && (
              <ModalAction onClick={() => handleModalAction("view")}>
                View Profile Photo
              </ModalAction>
            )}

            <ModalAction onClick={() => handleModalAction("change")}>
              {isDefaultImage ? "Upload Profile Photo" : "Change Profile Photo"}
            </ModalAction>

            {!isDefaultImage && (
              <ModalAction danger onClick={() => handleModalAction("delete")}>
                Remove Profile Photo
              </ModalAction>
            )}

            <ModalAction onClick={() => setIsModalOpen(false)}>
              Cancel
            </ModalAction>
          </ModalContent>
        </ModalOverlay>
      )}

      {isImageViewOpen && (
        <ModalOverlay onClick={() => setIsImageViewOpen(false)}>
          <img src={user.profilePic} style={{ maxWidth: '90%', maxHeight: '90vh', borderRadius: '8px' }} alt="Full View" />
        </ModalOverlay>
      )}

      {isCropModalOpen && previewImage && (
        <CropModalContainer>
          <div className="crop-wrap">
            <Cropper
              image={previewImage}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={(_, pixels) => setCroppedAreaPixels(pixels)}
            />
          </div>
          <div className="controls">
            <input type="range" min={1} max={3} step={0.1} value={zoom} onChange={e => setZoom(Number(e.target.value))} />
            <div className="buttons">
              <button onClick={handleCropSave}>Save</button>
              <button className="cancel" onClick={() => setIsCropModalOpen(false)}>Cancel</button>
            </div>
          </div>
        </CropModalContainer>
      )}
      {/* Existing Remove Photo Button */}
      {
        !isDefaultImage && (
          <ModalAction danger onClick={() => handleModalAction("delete")}>
            Remove Profile Photo
          </ModalAction>
        )
      }

      {/* New Confirmation Dialog */}
      <ConfirmationDialog
        show={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onCancel={() => setIsDeleteConfirmOpen(false)}
        onConfirm={confirmDelete}
        message="Are you sure you want to remove your profile photo?"
        confirmText="Remove"
        cancelText="Keep Photo"
      />
    </PageContainer>
  );
};

// --- Sub Components for Fields ---

// --- Sub Components for Fields ---

const EditableField = ({
  label,
  value,
  isEditing,
  tempValue,
  onEdit,
  inputType,
  onChange,
  onSave,   // New Prop
  onCancel, // New Prop
  type = "text",
  options = {},
  maxLength
}: any) => (
  <FieldWrapper>
    <Label>{label}</Label>
    {isEditing ? (
      <div style={{ display: "flex", alignItems: "center", gap: "8px", width: "100%" }}>
        {type === 'select' ? (
          <StyledSelect
            value={tempValue ?? value}
            onChange={e => onChange(e.target.value)}
            style={{ flex: 1 }}
          >
            <option value="" disabled>Select {label}</option>
            {Object.entries(options).map(([k, v]: any) => <option key={v} value={v}>{k}</option>)}
          </StyledSelect>
        ) : (
          <StyledInput
            autoFocus
            type={inputType || type}
            maxLength={maxLength}
            value={tempValue ?? value}
            onChange={e => onChange(e.target.value)}
            style={{ flex: 1 }}
          />
        )}
        {/* Inline Action Buttons */}
        <ActionButton className="save" onClick={onSave} title="Save">
          <FaCheck size={12} />
        </ActionButton>
        <ActionButton className="cancel" onClick={onCancel} title="Cancel">
          <FaTimes size={12} />
        </ActionButton>
      </div>
    ) : (
      <ValueDisplay>
        <span>{value || "N/A"}</span>
        <EditBtn onClick={onEdit}><FaEdit /></EditBtn>
      </ValueDisplay>
    )}
  </FieldWrapper>
);

const ReadOnlyField = ({ label, value }: any) => (
  <FieldWrapper>
    <Label>{label}</Label>
    <ValueDisplay readOnly>
      <span>{value || "N/A"}</span>
    </ValueDisplay>
  </FieldWrapper>
);

export default UserProfilePage;

// --- Styled Components ---

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  background-color: var(--page-color);
  min-height: 100vh;
  font-family: 'Inter', sans-serif;

  @media (max-width: 768px) {
    padding: 16px;
    gap: 16px;
  }
`;

const HeaderSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
`;

const ProfileCard = styled.div`
  flex: 1;
  min-width: 300px;
  background: var(--card-background);
  border-radius: 16px;
  padding: 24px;
  display: flex;
  align-items: center;
  gap: 24px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);

  @media (max-width: 480px) {
    flex-direction: column;
    text-align: center;
  }
`;

const ProfileImageWrapper = styled.div`
  position: relative;
  width: 100px;
  height: 100px;
  flex-shrink: 0;
  
  img {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
    border: 3px solid #fff;
    box-shadow: 0 4px 6px rgba(0,0,0,0.1);
    cursor: pointer;
  }
`;

const EditIconBtn = styled.button`
  position: absolute;
  bottom: 0;
  right: 0;
  background: #094752;
  color: white;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  &:hover { transform: scale(1.1); }
`;

const ProfileInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Name = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-color);
  margin: 0;
`;

const Email = styled.p`
  color: #666;
  font-size: 0.9rem;
  margin: 0;
`;

const StreakBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #fff3e0;
  color: #f97316;
  padding: 6px 12px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.85rem;
  width: fit-content;
  
  @media (max-width: 480px) {
    margin: 0 auto;
  }
`;

const StatsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  flex: 2;
  min-width: 300px;
`;

const StatCard = styled.div`
  flex: 1;
  min-width: 140px;
  background: var(--card-background);
  padding: 20px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
`;

const StatIcon = styled.div<{ color: string }>`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${props => props.color}20; // 20% opacity
  color: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
`;

const StatContent = styled.div`
  display: flex;
  flex-direction: column;
  label { font-size: 0.8rem; color: #888; text-transform: uppercase; letter-spacing: 0.5px; }
  strong { font-size: 1rem; color: var(--text-color); margin-top: 4px; }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 24px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SectionCard = styled.div`
  background: var(--card-background);
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  h3 { margin: 0; color: var(--text-color); font-size: 1.1rem; border-left: 4px solid #094752; padding-left: 12px; }
`;

const ActionButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &.save { background: #10b981; color: white; }
  &.cancel { background: #ef4444; color: white; }
`;

const FieldsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  align-items: start;
`;

const CorporateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
`;

const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  color: #999;
`;

const ValueDisplay = styled.div<{ readOnly?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
  
  span {
    color: var(--text-color);
    font-size: 0.95rem;
    font-weight: 500;
  }
  
  ${props => !props.readOnly && `
    &:hover {
      border-bottom-color: #094752;
      button { opacity: 1; }
    }
  `}
`;

const EditBtn = styled.button`
  background: none;
  border: none;
  color: #094752;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s;
  padding: 4px;
  font-size: 0.9rem;
`;

const StyledInput = styled.input`
  padding: 10px;
  border-radius: 8px;
  border: 1px solid #ddd;
  font-size: 0.95rem;
  background: #f9f9f9;
  &:focus { outline: none; border-color: #094752; background: white; }
`;

const StyledSelect = styled.select`
  padding: 10px;
  border-radius: 8px;
  border: 1px solid #ddd;
  font-size: 0.95rem;
  background: #f9f9f9;
  &:focus { outline: none; border-color: #094752; background: white; }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(2px);
`;

const ModalContent = styled.div`
  background: white;
  padding: 24px;
  border-radius: 16px;
  width: 90%;
  max-width: 350px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: 0 10px 25px rgba(0,0,0,0.1);
`;

const ModalTitle = styled.h4`
  margin: 0 0 8px 0;
  text-align: center;
  color: #333;
`;

const ModalAction = styled.button<{ danger?: boolean }>`
  padding: 12px;
  border: 1px solid #eee;
  border-radius: 10px;
  background: white;
  color: ${props => props.danger ? '#ef4444' : '#333'};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.danger ? '#fef2f2' : '#f9fafb'};
    border-color: ${props => props.danger ? '#fecaca' : '#ddd'};
  }
`;

const CropModalContainer = styled.div`
  position: fixed;
  inset: 0;
  background: black;
  z-index: 3000;
  display: flex;
  flex-direction: column;
  
  .crop-wrap {
    flex: 1;
    position: relative;
  }
  
  .controls {
    height: 100px;
    background: #222;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding: 20px;
    
    input[type=range] { width: 80%; max-width: 400px; }
    
    .buttons {
      display: flex;
      gap: 16px;
      button {
        padding: 8px 24px;
        border-radius: 20px;
        border: none;
        background: white;
        font-weight: 600;
        cursor: pointer;
        &.cancel { background: #444; color: white; }
      }
    }
  }
`;